import ContactsView from '../views/Contacts';
import AdminView from '../views/Admin';
import FollowUpView from '../views/FollowUp';
import FollowUpDetails from '../views/FollowUpDetails';
import * as PERMISSIONS from './permissions';
import { iconFollowup } from '../assets/icons/icon-followup';
import { iconBroadcast } from '../assets/icons/icon-broadcasts';
import { iconContracts } from '../assets/icons/icon-contacts';
import { iconEvents } from '../assets/icons/icon-events';
import { iconHome } from '../assets/icons/icon-home';
import { iconSwadhyay } from '../assets/icons/icon-swadhyay';
import { iconMedia } from '../assets/icons/icon-media';
import { iconShield } from '../assets/icons/icon-shield';
import { YogShield } from '../assets/icons/icon-yog-shield';
import { YogAdminContainer } from '../views/Admin/YogAdmin/YogAdminContainer';
import { iconFeedback } from '../assets/icons/icon-feedback';

const samparkBaseUrl = '/sampark';

/**
 * NEW SAMPARK ROUTES
 */

export interface SAMPARK_ROUTE {
  name: string;
  value: string;
  component: React.ReactNode;
  hideHeader?: boolean;
  hideInNav?: boolean;
  permission?: string;
  icon: React.ReactNode;
  search?: boolean;
  /**
   * If set, this route lives in sampark-new. The side nav renders a plain anchor to
   * this path so the browser does a full page load, rather than a client-side router
   * transition that would 404 in the legacy app.
   *
   * Both apps are served from one origin (see firebase.json: sampark-new is mounted
   * at /app/**), so these are plain same-origin paths.
   *
   * Mirrors `legacyUrl` in sampark-new's navigationConfig.ts, which points the other way.
   */
  newAppUrl?: string;
}

// Other routes
export const ADMIN: SAMPARK_ROUTE = {
  name: 'Admin',
  value: `${samparkBaseUrl}/admin`,
  component: AdminView,
  permission: PERMISSIONS.CONTACTS,
  icon: iconShield
};

export const FOLLOW_UP_DETAILS = {
  name: 'Follow Up Details',
  value: `${samparkBaseUrl}/followup/:id`,
  component: FollowUpDetails,
  hideHeader: true,
  hideInNav: true,
  permission: PERMISSIONS.FOLLOWUP,
  icon: iconFollowup
};

export const FOLLOW_UP = {
  name: 'Follow Up',
  value: `${samparkBaseUrl}/followup`,
  component: FollowUpView,
  permission: PERMISSIONS.FOLLOWUP,
  icon: iconFollowup
};

export const SIGN_IN = {
  name: 'Sign In',
  value: `/auth`
};

export const SIGNOUT = {
  name: 'Sign out',
  value: `/auth/signout`
};

export const BROADCAST = {
  name: 'Broadcast',
  value: `${samparkBaseUrl}/broadcast`,
  permission: PERMISSIONS.BROADCAST,
  component: null, // rendered by sampark-new,
  search: false,
  icon: iconBroadcast,
  newAppUrl: '/app/broadcast'
};

export const CONTACTS = {
  name: 'Contacts',
  value: `${samparkBaseUrl}/contacts`,
  permission: PERMISSIONS.CONTACTS,
  component: ContactsView,
  icon: iconContracts,
  search: true
};

export const EVENTS = {
  name: 'Events',
  value: `${samparkBaseUrl}/events`,
  permission: PERMISSIONS.EVENTS,
  component: null, // rendered by sampark-new,
  search: false,
  icon: iconEvents,
  newAppUrl: '/app/events'
};

export const HOME = {
  name: 'Home',
  value: `${samparkBaseUrl}/home`,
  permission: PERMISSIONS.CONTACTS,
  component: null, // rendered by sampark-new,
  icon: iconHome,
  search: true,
  // The new app's home is its route root ('/'), not '/home'.
  newAppUrl: '/app/'
};

export const SWADHYAY = {
  name: 'Swadhyay',
  value: `${samparkBaseUrl}/swadhyay`,
  permission: PERMISSIONS.SWADHYAY,
  component: null, // rendered by sampark-new,
  icon: iconSwadhyay,
  search: false,
  newAppUrl: '/app/swadhyay'
};

export const SMRUTI_DARSHAN = {
  name: 'Smruti Darshan',
  value: `${samparkBaseUrl}/smruti_darshan`,
  permission: PERMISSIONS.SMRUTI,
  component: null, // rendered by sampark-new,
  icon: iconMedia,
  search: false,
  // Legacy path uses an underscore; the new app uses a hyphen.
  newAppUrl: '/app/smruti-darshan'
};

export const CAMPAIGNS = {
  name: 'Campaigns',
  value: `${samparkBaseUrl}/campaigns`,
  permission: PERMISSIONS.CAMPAIGNS,
  component: null, // rendered by sampark-new,
  icon: iconMedia,
  search: false,
  newAppUrl: '/app/campaigns'
};

// Reached via the Admin tab row (/sampark/admin/yogAdmin), not the sidebar.
// This route stays registered so existing /sampark/yog-admin links keep working.
export const YOG_ADMIN: SAMPARK_ROUTE = {
  name: 'Yog Admin',
  value: `${samparkBaseUrl}/yog-admin`,
  component: YogAdminContainer,
  permission: PERMISSIONS.CONTACTS,
  icon: YogShield,
  hideInNav: true
};

export const FEEDBACK: SAMPARK_ROUTE = {
  name: 'Feedback',
  value: '/app/feedback',
  component: null, // rendered by sampark-new, not the legacy router
  permission: PERMISSIONS.FEEDBACK,
  icon: iconFeedback,
  newAppUrl: '/app/feedback'
};

// Access management lives entirely in sampark-new; this is a sidebar pointer to it.
// Landing on the catalog, whose tab row also exposes User Access.
export const ACCESS: SAMPARK_ROUTE = {
  name: 'Access',
  value: '/app/access/catalogs',
  component: null, // rendered by sampark-new,
  permission: PERMISSIONS.CONTACTS,
  icon: iconShield,
  newAppUrl: '/app/access/catalogs'
};

export const allPermissionRoutes: SAMPARK_ROUTE[] = [
  HOME,
  BROADCAST,
  CONTACTS,
  EVENTS,
  SWADHYAY,
  SMRUTI_DARSHAN,
  CAMPAIGNS,
  ADMIN,
  YOG_ADMIN,
  ACCESS,
  FOLLOW_UP_DETAILS,
  FOLLOW_UP,
  FEEDBACK
];
